import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { getUsers, createUser, updateUser, deleteUser } from '../../../services/users';
import { getOrganizations } from '../../../services/organizations';
import DataTable from '../../../components/Tables/DataTable';
import Modal from '../../../components/Common/Modal';
import Input from '../../../components/Forms/Input';
import Select from '../../../components/Forms/Select';
import Toast from '../../../components/Common/Toast';
import { 
  UserPlus, 
  ShieldCheck, 
  Building, 
  Mail, 
  Phone, 
  Edit3, 
  Trash2, 
  UserCog,
  Search
} from 'lucide-react';

const SuperAdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      full_name: '',
      email: '',
      phone: '',
      id_number: '',
      role: 'org_admin',
      organization_id: '',
      password: '',
      is_active: true,
    }
  });

  const selectedRole = watch('role');

  useEffect(() => {
    loadUsers();
    loadOrganizations();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await getUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to load users' });
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizations = async () => {
    try {
      const data = await getOrganizations();
      const orgs = Array.isArray(data) ? data : (data?.data || []);
      setOrganizations(orgs);
    } catch (err) {
      console.error('Failed to load organizations', err);
    }
  };

  // Helper to get organization name from ID
  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org ? `${org.name} (${org.org_code})` : 'Unknown Organization';
  };

  const onSubmit = async (data) => {
    try {
      const payload = { ...data };
      if (payload.role !== 'org_admin') {
        delete payload.organization_id;
      }

      if (editingUser) {
        await updateUser(editingUser.id, payload);
        setToast({ type: 'success', message: 'User profile updated' });
      } else {
        await createUser(payload);
        setToast({ type: 'success', message: 'Account created successfully' });
      }
      setShowModal(false);
      reset();
      loadUsers();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Operation failed' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user? This access cannot be recovered.')) {
      try {
        await deleteUser(id);
        setToast({ type: 'success', message: 'User removed' });
        loadUsers();
      } catch (err) {
        setToast({ type: 'error', message: 'Delete failed' });
      }
    }
  };

  const filteredUsers = users.filter(u => 
    u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Define columns with correct render signature (value, row)
  const columns = [
    { 
      key: 'full_name', 
      label: 'User Details',
      render: (value, row) => (
        <div className="flex items-center gap-3 py-1">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center border shrink-0 ${
            row.role === 'super_admin' ? 'bg-indigo-50 border-indigo-100 text-indigo-600' : 'bg-slate-50 border-slate-200 text-slate-500'
          }`}>
            {row.role === 'super_admin' ? <ShieldCheck size={20} /> : <UserCog size={20} />}
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-bold text-slate-800 truncate leading-tight">{value}</span>
            <div className="flex items-center gap-1.5 text-[10px] font-medium text-slate-400 uppercase tracking-widest mt-0.5">
              <Mail size={10} /> {row.email}
            </div>
          </div>
        </div>
      )
    },
    { 
      key: 'role', 
      label: 'Role & Scope',
      render: (value, row) => {
        const isSuper = value === 'super_admin';
        return (
          <div className="flex flex-col gap-1">
            <span className={`inline-flex items-center w-fit px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-tighter ${
              isSuper ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-white'
            }`}>
              {value?.replace('_', ' ')}
            </span>
            {!isSuper && row.organization_id && (
              <span className="text-[11px] text-slate-500 flex items-center gap-1">
                <Building size={10} /> {getOrgName(row.organization_id)}
              </span>
            )}
          </div>
        );
      }
    },
    { 
      key: 'is_active', 
      label: 'Status',
      render: (value) => (
        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
          value ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-rose-50 text-rose-700 border-rose-200'
        }`}>
          {value ? 'Active' : 'Suspended'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div className="flex items-center justify-end gap-1">
          <button
            title="Edit User"
            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
            onClick={() => { setEditingUser(row); reset(row); setShowModal(true); }}
          >
            <Edit3 size={18} />
          </button>
          <button
            title="Remove User"
            className="p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
            onClick={() => handleDelete(row.id)}
          >
            <Trash2 size={18} />
          </button>
        </div>
      ),
    },
  ];

  const roleOptions = [
    { value: 'super_admin', label: 'Super Admin (All Orgs)' },
    { value: 'org_admin', label: 'Organization Admin (Scoped)' },
  ];

  const orgOptions = organizations.map(org => ({
    value: org.id,
    label: `${org.name} (${org.org_code})`,
  }));

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 pb-8">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight italic">System Personnel</h1>
          <p className="text-slate-500 font-medium mt-1">Audit and configure access levels for the entire registry.</p>
        </div>
        <button
          className="bg-slate-900 text-white px-6 py-3.5 rounded-2xl font-bold flex items-center gap-2 hover:bg-indigo-600 transition-all shadow-xl shadow-slate-200 active:scale-95"
          onClick={() => {
            setEditingUser(null);
            reset({ role: 'org_admin', is_active: true, full_name: '', email: '', phone: '', id_number: '', organization_id: '', password: '' });
            setShowModal(true);
          }}
        >
          <UserPlus size={20} /> Authorize New User
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center bg-white p-2 rounded-2xl border border-slate-200 shadow-sm">
        <div className="relative flex-1 group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
          <input 
            type="text" 
            placeholder="Filter by name or email address..."
            className="w-full pl-12 pr-4 py-3 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-indigo-500/20 transition-all text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-[2rem] border border-slate-200 shadow-xl overflow-hidden min-h-[400px]">
        <DataTable columns={columns} data={filteredUsers} loading={loading} />
      </div>

      {/* Modal Form */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingUser ? 'Modify Access Credentials' : 'New User Authorization'}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4 px-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input name="full_name" control={control} label="Legal Name" placeholder="John Doe" rules={{ required: 'Full name required' }} />
            <Input name="email" control={control} label="System Email" placeholder="john@example.com" rules={{ required: 'Email required' }} />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input name="phone" control={control} label="Primary Phone" placeholder="+254..." />
            <Input name="id_number" control={control} label="ID / Passport Number" />
          </div>

          <div className="border-t border-slate-100 pt-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select name="role" control={control} label="Permission Level" options={roleOptions} rules={{ required: 'Role required' }} />
              <Select name="is_active" control={control} label="Account Status" options={[{ value: true, label: 'Active' }, { value: false, label: 'Suspended' }]} />
            </div>
          </div>

          {selectedRole === 'org_admin' && (
            <div className="bg-indigo-50/50 p-4 rounded-2xl border border-indigo-100 animate-in fade-in slide-in-from-top-2">
              <Select
                name="organization_id"
                control={control}
                label="Assign to Organization"
                options={orgOptions}
                rules={{ required: 'Required for Org Admins' }}
              />
              <p className="text-[10px] text-indigo-400 font-bold uppercase mt-2">Scope: User will only see data for this entity.</p>
            </div>
          )}

          {!editingUser && (
            <Input name="password" control={control} label="Initial Password" type="password" placeholder="Min 6 characters" rules={{ required: 'Password required', minLength: 6 }} />
          )}

          <div className="flex gap-3 pt-6">
            <button
              type="submit"
              className="flex-1 py-4 bg-slate-900 text-white font-black rounded-2xl hover:bg-indigo-600 transition-all shadow-lg active:scale-95"
            >
              {editingUser ? 'Commit Changes' : 'Confirm & Authorize'}
            </button>
            <button
              type="button"
              className="px-6 py-4 bg-slate-100 text-slate-500 font-bold rounded-2xl hover:bg-slate-200 transition-all"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
};

export default SuperAdminUsers;